import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatDate, type Course, type CourseEdition, type Enrollment } from "@/lib/api";
import SignatureUpload from "@/components/feature/SignatureUpload";
import ListingFilters from "@/components/feature/ListingFilters";
import { emptyListingFilters, matchesMonth, type ListingFilterState } from "@/lib/listingFilters";

export default function EvaluadorPortalPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [editions, setEditions] = useState<CourseEdition[]>([]);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<ListingFilterState>(emptyListingFilters);
  const [instructorFilter, setInstructorFilter] = useState("");
  const [evaluatorFilter, setEvaluatorFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [editionsData, enrollmentsData] = await Promise.all([
          api.getEditions(),
          api.getEnrollments(),
        ]);
        setEditions(editionsData);
        setEnrollments(enrollmentsData);
      } catch {
        setEditions([]);
        setEnrollments([]);
      }
      setLoading(false);
    })();
  }, []);

  const courses = useMemo(() => {
    const map = new Map<string, Course>();
    for (const ed of editions) {
      if (ed.course && !map.has(ed.course.id)) map.set(ed.course.id, ed.course);
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, "es"));
  }, [editions]);

  const instructors = useMemo(() => {
    const map = new Map<string, string>();
    for (const ed of editions) {
      if (ed.instructor_id && ed.instructor?.full_name) {
        map.set(ed.instructor_id, ed.instructor.full_name);
      }
    }
    return Array.from(map.entries())
      .map(([id, full_name]) => ({ id, full_name }))
      .sort((a, b) => a.full_name.localeCompare(b.full_name, "es"));
  }, [editions]);

  const evaluators = useMemo(() => {
    const map = new Map<string, string>();
    for (const ed of editions) {
      if (ed.evaluator_id && ed.evaluator?.full_name) {
        map.set(ed.evaluator_id, ed.evaluator.full_name);
      }
    }
    return Array.from(map.entries())
      .map(([id, full_name]) => ({ id, full_name }))
      .sort((a, b) => a.full_name.localeCompare(b.full_name, "es"));
  }, [editions]);

  const filteredEditions = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return editions.filter((ed) => {
      if (filters.courseId && ed.course_id !== filters.courseId) return false;
      if (filters.editionId && ed.id !== filters.editionId) return false;
      if (filters.category && ed.course?.category !== filters.category) return false;
      if (filters.month && !matchesMonth(ed.start_date, filters.month)) return false;
      if (instructorFilter && ed.instructor_id !== instructorFilter) return false;
      if (evaluatorFilter && ed.evaluator_id !== evaluatorFilter) return false;
      if (statusFilter && ed.status !== statusFilter) return false;
      if (q) {
        const haystack = [
          ed.course?.name,
          ed.edition_label,
          ed.location,
          ed.instructor?.full_name,
          ed.evaluator?.full_name,
          ed.course?.category,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [editions, filters, instructorFilter, evaluatorFilter, statusFilter]);

  const hasExtraFilters = Boolean(instructorFilter || evaluatorFilter || statusFilter);

  const enrollmentEditionId = (enrollment: Enrollment) => enrollment.edition_id || enrollment.diplomado_edition_id || "";

  const getEditionEnrollments = (editionId: string) =>
    enrollments.filter((e) => enrollmentEditionId(e) === editionId);

  const getPendingEvals = (editionId: string) =>
    getEditionEnrollments(editionId).filter(
      (e) => e.attendance_status === "presente" && e.evaluation_status === "pendiente"
    ).length;

  const getApproved = (editionId: string) =>
    getEditionEnrollments(editionId).filter((e) => e.evaluation_status === "aprobado").length;

  const getRejected = (editionId: string) =>
    getEditionEnrollments(editionId).filter((e) => e.evaluation_status === "rechazado").length;

  const getReviewCount = (editionId: string) =>
    getEditionEnrollments(editionId).filter((e) => e.evaluation_status === "revision").length;

  const totalPending = filteredEditions.reduce((sum, ed) => sum + getPendingEvals(ed.id), 0);
  const totalApproved = filteredEditions.reduce((sum, ed) => sum + getApproved(ed.id), 0);

  const hasFilters =
    Boolean(filters.search.trim() || filters.courseId || filters.editionId || filters.category || filters.month) ||
    hasExtraFilters;

  return (
    <div className="space-y-6 animate-[fadeIn_0.3s_ease-out]">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">Portal del Evaluador</h1>
          <p className="text-sm text-[var(--color-text-muted)] mt-1">
            Bienvenido, {user?.full_name} &mdash; Revise y apruebe estudiantes para certificación
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[var(--color-accent-oil)]/15 border border-[var(--color-accent-oil)]/20 flex items-center justify-center flex-shrink-0">
            <i className="ri-check-double-line text-[var(--color-accent-oil)] text-xl"></i>
          </div>
          <div>
            <p className="text-2xl font-bold text-[var(--color-text-primary)]">{filteredEditions.length}</p>
            <p className="text-xs text-[var(--color-text-muted)]">Ediciones{hasFilters ? " (filtro)" : " a Evaluar"}</p>
          </div>
        </div>
        <div className="card flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-yellow-500/15 border border-yellow-500/20 flex items-center justify-center flex-shrink-0">
            <i className="ri-alert-line text-yellow-400 text-xl"></i>
          </div>
          <div>
            <p className="text-2xl font-bold text-yellow-400">{totalPending}</p>
            <p className="text-xs text-[var(--color-text-muted)]">Pendientes</p>
          </div>
        </div>
        <div className="card flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-green-500/15 border border-green-500/20 flex items-center justify-center flex-shrink-0">
            <i className="ri-check-line text-green-400 text-xl"></i>
          </div>
          <div>
            <p className="text-2xl font-bold text-green-400">{totalApproved}</p>
            <p className="text-xs text-[var(--color-text-muted)]">Aprobados</p>
          </div>
        </div>
        <div className="card flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[var(--color-bg-tertiary)] flex items-center justify-center flex-shrink-0">
            <i className="ri-group-line text-[var(--color-text-muted)] text-xl"></i>
          </div>
          <div>
            <p className="text-2xl font-bold text-[var(--color-text-primary)]">
              {filteredEditions.reduce((sum, ed) => sum + getEditionEnrollments(ed.id).length, 0)}
            </p>
            <p className="text-xs text-[var(--color-text-muted)]">Total Estudiantes</p>
          </div>
        </div>
      </div>

      <SignatureUpload />

      {!loading && editions.length > 0 && (
        <ListingFilters
          title="Filtros de ediciones"
          filters={filters}
          onChange={setFilters}
          courses={courses}
          editions={editions}
          searchPlaceholder="Curso, edición, persona..."
          resultCount={filteredEditions.length}
          totalCount={editions.length}
          hint="Mes filtra por fecha de inicio de la edición."
          extraActive={hasExtraFilters}
          onClearExtras={() => {
            setInstructorFilter("");
            setEvaluatorFilter("");
            setStatusFilter("");
          }}
          extras={
            <>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mb-1 block">Instructor</label>
                <select
                  className="input-field"
                  value={instructorFilter}
                  onChange={(e) => setInstructorFilter(e.target.value)}
                  aria-label="Filtrar por instructor"
                >
                  <option value="">Todos los instructores</option>
                  {instructors.map((i) => (
                    <option key={i.id} value={i.id}>{i.full_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mb-1 block">Evaluador</label>
                <select
                  className="input-field"
                  value={evaluatorFilter}
                  onChange={(e) => setEvaluatorFilter(e.target.value)}
                  aria-label="Filtrar por evaluador"
                >
                  <option value="">Todos los evaluadores</option>
                  {evaluators.map((e) => (
                    <option key={e.id} value={e.id}>{e.full_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mb-1 block">Estado</label>
                <select
                  className="input-field"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  aria-label="Filtrar por estado"
                >
                  <option value="">Todos los estados</option>
                  <option value="programado">Programado</option>
                  <option value="en_curso">En curso</option>
                  <option value="completado">Completado</option>
                  <option value="cancelado">Cancelado</option>
                </select>
              </div>
            </>
          }
        />
      )}

      {loading ? (
        <div className="card text-center py-16">
          <i className="ri-loader-4-line text-3xl text-[var(--color-text-muted)] animate-spin"></i>
          <p className="text-sm text-[var(--color-text-muted)] mt-3">Cargando ediciones...</p>
        </div>
      ) : editions.length === 0 ? (
        <div className="card text-center py-16">
          <i className="ri-check-double-line text-4xl text-[var(--color-text-muted)]"></i>
          <p className="text-sm text-[var(--color-text-muted)] mt-3">
            No tiene ediciones asignadas para evaluación.
          </p>
        </div>
      ) : filteredEditions.length === 0 ? (
        <div className="card text-center py-16">
          <i className="ri-filter-off-line text-4xl text-[var(--color-text-muted)]"></i>
          <p className="text-sm text-[var(--color-text-muted)] mt-3">
            Ninguna edición coincide con los filtros.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredEditions.map((edition) => {
            const total = getEditionEnrollments(edition.id).length;
            const pending = getPendingEvals(edition.id);
            const approved = getApproved(edition.id);
            const rejected = getRejected(edition.id);
            const reviews = getReviewCount(edition.id);
            const progress = total > 0 ? Math.round(((approved + rejected) / total) * 100) : 0;
            const courseName = edition.course?.name || "Curso";

            return (
              <div
                key={edition.id}
                onClick={() => navigate(`/portal-evaluador/curso/${edition.id}`)}
                className="card hover:border-[var(--color-border-light)] cursor-pointer group transition-all duration-300 hover:-translate-y-1"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 min-w-0 pr-3">
                    <h3 className="text-sm font-semibold text-[var(--color-text-primary)] line-clamp-2">
                      {courseName}
                    </h3>
                    <p className="text-xs text-[var(--color-text-muted)] mt-1">
                      {edition.edition_label ? `${edition.edition_label} · ` : ""}
                      {formatDate(edition.start_date)} &mdash; {formatDate(edition.end_date)} | {edition.location.split(" - ")[0]}
                    </p>
                    <div className="mt-2 space-y-1">
                      <p className="text-xs text-[var(--color-text-secondary)] flex items-center gap-1.5">
                        <i className="ri-user-star-line text-[var(--color-accent-oil)]"></i>
                        <span className="text-[var(--color-text-muted)]">Instructor:</span>
                        <span className="truncate">{edition.instructor?.full_name || "Sin asignar"}</span>
                      </p>
                      <p className="text-xs text-[var(--color-text-secondary)] flex items-center gap-1.5">
                        <i className="ri-user-follow-line text-[var(--color-accent)]"></i>
                        <span className="text-[var(--color-text-muted)]">Evaluador:</span>
                        <span className="truncate">{edition.evaluator?.full_name || "Sin asignar"}</span>
                      </p>
                    </div>
                  </div>
                  {pending > 0 ? (
                    <span className="badge-warning flex-shrink-0">{pending} pend.</span>
                  ) : approved + rejected > 0 ? (
                    <span className="badge-success flex-shrink-0">Completado</span>
                  ) : null}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                  <div className="bg-[var(--color-bg-tertiary)] rounded-lg p-2.5 text-center">
                    <p className="text-lg font-bold text-[var(--color-text-primary)]">{total}</p>
                    <p className="text-[10px] text-[var(--color-text-muted)]">Total</p>
                  </div>
                  <div className="bg-[var(--color-bg-tertiary)] rounded-lg p-2.5 text-center">
                    <p className="text-lg font-bold text-yellow-400">{pending}</p>
                    <p className="text-[10px] text-[var(--color-text-muted)]">Pend.</p>
                  </div>
                  <div className="bg-[var(--color-bg-tertiary)] rounded-lg p-2.5 text-center">
                    <p className="text-lg font-bold text-green-400">{approved}</p>
                    <p className="text-[10px] text-[var(--color-text-muted)]">Aprob.</p>
                  </div>
                  <div className="bg-[var(--color-bg-tertiary)] rounded-lg p-2.5 text-center">
                    <p className="text-lg font-bold text-red-400">{rejected}</p>
                    <p className="text-[10px] text-[var(--color-text-muted)]">Rechaz.</p>
                  </div>
                </div>

                {reviews > 0 && (
                  <div className="flex items-center gap-2 text-xs mb-3">
                    <i className="ri-loop-left-line text-yellow-400"></i>
                    <span className="text-yellow-400">{reviews} en revisión</span>
                  </div>
                )}

                <div className="border-t border-[var(--color-border)] pt-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs text-[var(--color-text-muted)]">Progreso de evaluación</span>
                    <span className="text-xs font-medium text-[var(--color-text-primary)]">{progress}%</span>
                  </div>
                  <div className="w-full h-2 bg-[var(--color-bg-tertiary)] rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        progress >= 100 ? "bg-green-400" : progress >= 50 ? "bg-[var(--color-accent-oil)]" : "bg-yellow-400"
                      }`}
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
